import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { useLang } from "../i18n";
import { IMAGES, CONTACT } from "../lib/data";

const links = [
  { to: "/", key: "nav.home" },
  { to: "/services", key: "nav.services" },
  { to: "/fleet", key: "nav.fleet" },
  { to: "/destinations", key: "nav.destinations" },
  { to: "/about", key: "nav.about" },
  { to: "/contacts", key: "nav.contacts" },
];

export const Navbar = () => {
  const { t, lang, changeLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-matte/80 backdrop-blur-xl border-b border-white/10 py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 lg:px-8">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
          <img src={IMAGES.logo} alt="NC&C Luxury" className="h-10 w-auto object-contain md:h-12" />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={({ isActive }) =>
                `font-body text-xs uppercase tracking-[0.18em] transition-colors duration-300 ${
                  isActive ? "text-gold" : "text-white/70 hover:text-white"
                }`
              }
            >
              {t(l.key)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-5 lg:flex">
          <div className="flex items-center gap-1 font-body text-xs tracking-widest">
            {["it", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}`}
                onClick={() => changeLang(l)}
                className={`px-1.5 uppercase transition-colors ${lang === l ? "text-gold" : "text-white/50 hover:text-white"}`}
              >
                {l}
              </button>
            ))}
          </div>
          <Link
            to="/booking"
            data-testid="nav-booking-cta"
            className="bg-gold px-6 py-2.5 font-body text-xs uppercase tracking-[0.18em] text-black transition-colors duration-300 hover:bg-gold-muted"
          >
            {t("nav.booking")}
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className="text-white lg:hidden"
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="border-t border-white/10 bg-matte/95 backdrop-blur-xl lg:hidden">
          <div className="flex flex-col px-6 py-6">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `border-b border-white/5 py-4 font-body text-sm uppercase tracking-widest ${isActive ? "text-gold" : "text-white/80"}`
                }
              >
                {t(l.key)}
              </NavLink>
            ))}
            <div className="mt-5 flex items-center justify-between">
              <div className="flex items-center gap-2 font-body text-sm">
                {["it", "en"].map((l) => (
                  <button key={l} data-testid={`lang-mobile-${l}`} onClick={() => changeLang(l)}
                    className={`px-2 uppercase ${lang === l ? "text-gold" : "text-white/50"}`}>{l}</button>
                ))}
              </div>
              <a href={`tel:${CONTACT.phone}`} className="flex items-center gap-2 text-gold">
                <Phone size={16} /> <span className="font-body text-sm">{CONTACT.phoneIntl}</span>
              </a>
            </div>
            <Link to="/booking" data-testid="mobile-booking-cta"
              className="mt-5 bg-gold py-3 text-center font-body text-xs uppercase tracking-widest text-black">
              {t("nav.booking")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
